import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';
import Users from 'components/pages/management/Users';

const USER_ROW = {
  id: 1,
  name: 'Abe Martinez',
  email: 'martinez@email.com',
  username: 'amartinez',
  jobRole: 'Fleet Manager',
  accounts: ['Account Name A'],
  permissions: ['Fleet Administrator'],
  permissionAssignments: [
    { account: 'Account Name A', permission: 'Fleet Administrator' },
  ],
  lastLoginAt: '03/08/2025 05:22 PM MST',
};

// The page talks to three endpoints, so the mock dispatches on path rather than
// answering everything with the users payload.
const respondWith = (users, pagination = {}) => {
  global.fetch = jest.fn().mockImplementation((url) => {
    let body;

    if (url.startsWith('/management/users/filter_options')) {
      body = { permissions: ['Member'], jobRoles: [] };
    } else if (url.startsWith('/management/users/accounts')) {
      body = { accounts: [] };
    } else {
      body = {
        users,
        pagination: {
          page: 1,
          per_page: 25,
          total_entries: users.length,
          total_pages: 1,
          ...pagination,
        },
      };
    }

    return Promise.resolve({ ok: true, json: async () => body });
  });
};

beforeEach(() => {
  window.localStorage.clear();
  // Wide viewport so the LeftNav renders expanded; see ManagementLayout tests.
  window.matchMedia = (query) => ({
    matches: query === '(min-width: 1280px)',
    media: query,
    onchange: null,
    addListener: () => {},
    removeListener: () => {},
    addEventListener: () => {},
    removeEventListener: () => {},
    dispatchEvent: () => false,
  });
  respondWith([USER_ROW]);
});

afterEach(() => {
  delete global.fetch;
});

const renderPage = (props = {}) =>
  render(
    <ConfigProvider>
      <Users currentPath="/management/users" {...props} />
    </ConfigProvider>,
  );

describe('management/Users', () => {
  it('renders the page heading', () => {
    renderPage();

    expect(screen.getByRole('heading', { name: 'Users' })).toBeInTheDocument();
  });

  it('renders inside the management shell, with the nav present', () => {
    renderPage();

    expect(screen.getByRole('link', { name: /users/i })).toHaveAttribute(
      'href',
      '/management/users',
    );
  });

  it('offers the Create User menu with both options disabled — creating users is out of scope', () => {
    renderPage();

    userEvent.click(screen.getByRole('button', { name: /create user/i }));

    expect(screen.getByText('Create Multiple Users')).toBeInTheDocument();
  });

  it('loads the first page of users on mount', async () => {
    renderPage();

    await waitFor(() =>
      expect(screen.getByText('Abe Martinez')).toBeInTheDocument(),
    );

    expect(global.fetch).toHaveBeenCalledWith(
      '/management/users.json?page=1',
      expect.anything(),
    );
  });

  it('renders every column the row supplies', async () => {
    renderPage();

    await waitFor(() =>
      expect(screen.getByText('Abe Martinez')).toBeInTheDocument(),
    );

    expect(screen.getByText('martinez@email.com')).toBeInTheDocument();
    expect(screen.getByText('Account Name A')).toBeInTheDocument();
    expect(screen.getByText('Fleet Administrator')).toBeInTheDocument();
    expect(screen.getByText('Fleet Manager')).toBeInTheDocument();
    expect(screen.getByText('03/08/2025 05:22 PM MST')).toBeInTheDocument();
  });

  // Teams land with PD-4213; the mockup's column is deliberately absent here.
  it('does not render a Teams column', async () => {
    renderPage();

    await waitFor(() =>
      expect(screen.getByText('Abe Martinez')).toBeInTheDocument(),
    );

    expect(
      screen.queryByRole('columnheader', { name: /teams/i }),
    ).not.toBeInTheDocument();
  });

  describe('the empty state', () => {
    it('shows a message when no users come back', async () => {
      respondWith([]);

      renderPage();

      await waitFor(() =>
        expect(screen.getByText('No Users Yet')).toBeInTheDocument(),
      );
    });

    // Per Figma 787:78031 the genuinely-empty state drops both the column
    // headers and the pagination footer. The "No Matches" state keeps them, so
    // this assertion is what will keep the two apart once search lands.
    it('drops the column headers and the footer', async () => {
      respondWith([]);

      renderPage();

      await waitFor(() =>
        expect(screen.getByText('No Users Yet')).toBeInTheDocument(),
      );

      expect(screen.queryAllByRole('columnheader')).toHaveLength(0);
      expect(screen.queryByText(/of 0 Users/)).not.toBeInTheDocument();
    });
  });

  it('surfaces a fetch failure instead of an empty table', async () => {
    global.fetch = jest.fn().mockResolvedValue({ ok: false, status: 500 });

    renderPage();

    await waitFor(() =>
      expect(screen.getByText(/error loading users/i)).toBeInTheDocument(),
    );
  });
});
